import { Link } from "react-router-dom";
import { HardHat, Hammer, PaintBucket, Truck, Phone, MessageCircle } from "lucide-react";
import { SITE, waLink } from "@/config/site";
import { usePageTitle, Reveal, SectionHead, Eyebrow, RoadDash } from "@/components/bits";
import { QuoteCallout } from "./Home";

const HERO_IMG = "/work/excavation.jpg";

const SERVICES = [
  { icon: HardHat, title: "General Construction", copy: "Practical, well-built construction work for residential, commercial and government projects — planned properly and finished to a high standard." },
  { icon: Hammer, title: "Demolition", copy: "Safe, controlled demolition and site clearing, with debris removed and the site left ready for what comes next." },
  { icon: PaintBucket, title: "Refurbishment", copy: "Bringing tired buildings and spaces back to life — repairs, upgrades and finishing work handled end to end." },
  { icon: Truck, title: "Trucking & Haulage", copy: "Dependable trucking and haulage across Bermuda — materials, equipment and debris moved where they need to go, on schedule." },
];

export default function Construction() {
  usePageTitle("Construction Services");
  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/10" data-testid="construction-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-joell-black/70 via-joell-black/60 to-joell-black" aria-hidden="true" />
        <div className="container-x relative pb-20 pt-36 md:pb-28 md:pt-48">
          <Reveal>
            <Eyebrow>Bermuda Construction Contractor</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl">
              Construction services
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              From general construction and refurbishment to demolition and trucking, JOELL brings 18 years of
              hands-on experience to residential, commercial and government projects across Bermuda.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/request-quote" className="btn-accent !px-8 !py-4" data-testid="construction-quote-button">Request a Free Quote</Link>
              <a href={SITE.phoneHref} className="btn-outline !px-8 !py-4" data-testid="construction-call-button"><Phone size={15} /> Call {SITE.phone}</a>
            </div>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0"><RoadDash /></div>
      </section>

      <section className="container-x py-20 md:py-28" data-testid="construction-services-section">
        <SectionHead
          eyebrow="What we do"
          title="Built properly, finished cleanly"
          sub="Every project gets careful preparation, experienced workmanship and a finish JOELL is proud to put a name to."
        />
        <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div className="group h-full bg-joell-black p-8 transition-colors duration-300 hover:bg-joell-surface md:p-10" data-testid={`construction-service-card-${i}`}>
                <div className="flex items-start justify-between">
                  <s.icon size={30} strokeWidth={1.6} className="text-joell-yellow" />
                  <span className="font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-start justify-between gap-6 border border-joell-yellow/40 bg-joell-yellow/5 p-8 sm:p-10 md:flex-row md:items-center" data-testid="construction-cta">
            <div>
              <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">Planning a construction project?</h2>
              <p className="mt-2 text-slate-300">Tell us what you're building — we'll help you get it done right.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/request-quote" className="btn-accent" data-testid="construction-cta-quote-button">Get My Free Quote</Link>
              <a href={waLink("Hi JOELL, I have a construction project I'd like to discuss.")} target="_blank" rel="noreferrer" className="btn-outline" data-testid="construction-cta-whatsapp-button">
                <MessageCircle size={15} /> WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <QuoteCallout />
    </>
  );
}
